import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { alertApi } from '../../services/api';
import { formatNumber, formatDateTime } from '../../utils/formatters';

// ── Constants ────────────────────────────────────────────────────────────────

const SEVERITY_FILTERS = [
  { key: '', label: 'All' },
  { key: 'critical', label: 'Critical' },
  { key: 'warning', label: 'Warning' },
  { key: 'info', label: 'Info' },
];

const DAYS_FILTERS = [
  { key: 7, label: '7d' },
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
];

const PLATFORM_FILTERS = [
  { key: '', label: 'All Platforms' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'atv', label: 'Android TV' },
  { key: 'firestick', label: 'Fire TV' },
  { key: 'ios', label: 'iOS' },
];

const PLATFORM_CONFIG = {
  mobile: { label: 'Android Mobile', short: 'Mobile', color: 'bg-green-500/15 text-green-400 ring-1 ring-green-500/30' },
  atv: { label: 'Android TV', short: 'Android TV', color: 'bg-purple-500/15 text-purple-400 ring-1 ring-purple-500/30' },
  firestick: { label: 'Fire TV', short: 'Fire TV', color: 'bg-orange-500/15 text-orange-400 ring-1 ring-orange-500/30' },
  ios: { label: 'iOS', short: 'iOS', color: 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30' },
};

const SEVERITY_CONFIG = {
  critical: {
    dot: 'bg-red-500',
    badge: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30',
    card: 'bg-red-500/10 border border-red-500/20 rounded-xl p-3',
    icon: (
      <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 2l10 18H2L12 2z" />
      </svg>
    ),
    label: 'Action Required',
  },
  warning: {
    dot: 'bg-amber-500',
    badge: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',
    card: 'bg-amber-500/10 border border-amber-500/20 rounded-xl p-3',
    icon: (
      <svg className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    label: 'Monitor',
  },
  info: {
    dot: 'bg-blue-500',
    badge: 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30',
    card: 'bg-blue-500/10 border border-blue-500/20 rounded-xl p-3',
    icon: (
      <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
      </svg>
    ),
    label: 'For Review',
  },
};

const DETAIL_TABS = ['Overview', 'Recommendations', 'Related'];

// ── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function computeImpactScore(deviationRatio) {
  if (deviationRatio == null || deviationRatio <= 0) return 0;
  return Math.min(100, Math.round((deviationRatio / (deviationRatio + 1)) * 100));
}

function impactLevel(score) {
  if (score >= 70) return { label: 'High', cls: 'bg-red-500/15 text-red-400' };
  if (score >= 40) return { label: 'Medium', cls: 'bg-amber-500/15 text-amber-400' };
  return { label: 'Low', cls: 'bg-emerald-500/15 text-emerald-400' };
}

function enrichAlert(alert) {
  const threshold = alert.threshold ?? alert.threshold_value ?? 0;
  const value = alert.metric_value ?? alert.current_value ?? 0;
  const deviationRatio = threshold > 0 ? Math.abs(value - threshold) / threshold : 0;
  return {
    ...alert,
    threshold,
    value,
    deviationRatio,
    impact: computeImpactScore(deviationRatio),
  };
}

// ── Sub-components ───────────────────────────────────────────────────────────

function PlatformBadge({ platform, size = 'sm' }) {
  const cfg = PLATFORM_CONFIG[platform];
  if (!cfg) return <span className="text-[10px] text-gray-500">—</span>;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${cfg.color} ${size === 'xs' ? 'text-[9px] px-1' : ''}`}>
      {cfg.short}
    </span>
  );
}

function SummaryCard({ severity, count, topMessage, platformCounts }) {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
  return (
    <div className={cfg.card}>
      <div className="flex items-center gap-2 mb-1">
        {cfg.icon}
        <span className="text-xl font-bold text-gray-100">{count}</span>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 ml-auto">
          {cfg.label}
        </span>
      </div>
      {/* Platform breakdown chips */}
      {platformCounts && Object.keys(platformCounts).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {Object.entries(platformCounts).map(([plat, cnt]) => {
            const pcfg = PLATFORM_CONFIG[plat];
            if (!pcfg || cnt === 0) return null;
            return (
              <span key={plat} className="inline-flex items-center gap-1 bg-surface-700/60 rounded px-1.5 py-0.5">
                <span className={`text-[9px] font-bold ${pcfg.color.includes('green') ? 'text-green-400' : pcfg.color.includes('purple') ? 'text-purple-400' : pcfg.color.includes('orange') ? 'text-orange-400' : 'text-blue-400'}`}>
                  {pcfg.short}
                </span>
                <span className="text-[10px] font-semibold text-gray-300">{cnt}</span>
              </span>
            );
          })}
        </div>
      )}
      {topMessage && (
        <p className="text-[11px] text-gray-400 truncate">{topMessage}</p>
      )}
    </div>
  );
}

function FilterBar({
  severity, setSeverity,
  days, setDays,
  search, setSearch,
  platform, setPlatform,
  showAcknowledged, setShowAcknowledged,
  selectedCount, onBulkAcknowledge,
}) {
  return (
    <div className="sticky top-0 z-20 bg-surface-800/95 backdrop-blur-sm py-3 flex flex-wrap items-center gap-3">
      {/* Severity toggles */}
      <div className="flex items-center gap-1">
        {SEVERITY_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setSeverity(f.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              severity === f.key
                ? 'bg-brand-500/20 text-brand-400 ring-1 ring-brand-500/40'
                : 'text-gray-400 hover:text-gray-200 hover:bg-surface-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Platform filter */}
      <div className="flex items-center gap-1 border-l border-surface-600 pl-3">
        {PLATFORM_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setPlatform(f.key)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              platform === f.key
                ? 'bg-brand-500/20 text-brand-400 ring-1 ring-brand-500/40'
                : 'text-gray-400 hover:text-gray-200 hover:bg-surface-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Days selector */}
      <div className="flex items-center gap-1 border-l border-surface-600 pl-3">
        {DAYS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setDays(f.key)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              days === f.key
                ? 'bg-brand-500/20 text-brand-400 ring-1 ring-brand-500/40'
                : 'text-gray-400 hover:text-gray-200 hover:bg-surface-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search alerts..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="bg-surface-700 border border-surface-600 rounded-lg text-xs px-3 py-1.5 w-48 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-500/40"
      />

      {/* Show Acknowledged */}
      <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none ml-auto">
        <input
          type="checkbox"
          checked={showAcknowledged}
          onChange={(e) => setShowAcknowledged(e.target.checked)}
          className="w-3.5 h-3.5 rounded border-surface-600 bg-surface-700 text-brand-500 focus:ring-brand-500/40"
        />
        Show Acknowledged
      </label>

      {/* Bulk Acknowledge */}
      {selectedCount > 0 && (
        <button
          onClick={onBulkAcknowledge}
          className="px-3 py-1.5 rounded-lg bg-brand-500/20 text-brand-400 ring-1 ring-brand-500/40 text-xs font-medium hover:bg-brand-500/30 transition-colors"
        >
          Acknowledge ({selectedCount})
        </button>
      )}
    </div>
  );
}

function AlertTable({ alerts, selected, onToggleSelect, onToggleAll, onRowClick }) {
  const allSelected = alerts.length > 0 && selected.size === alerts.length;
  return (
    <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 overflow-hidden">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-surface-600/50">
            <th className="px-3 py-2.5 w-10">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                className="w-3.5 h-3.5 rounded border-surface-600 bg-surface-700 text-brand-500 focus:ring-brand-500/40"
              />
            </th>
            {['Severity', 'Platform', 'Alert Message', 'Version', 'Metric / Threshold', 'Impact', 'Time', 'Status'].map((h) => (
              <th key={h} className="px-3 py-2.5 text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {alerts.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center py-12 text-gray-500 text-sm">
                No alerts match your filters
              </td>
            </tr>
          )}
          {alerts.map((a) => {
            const sev = SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.info;
            const imp = impactLevel(a.impact);
            const isSelected = selected.has(a.id);
            const versionDisplay = a.app_version
              ? (a.version_code ? `${a.app_version} (${a.version_code})` : a.app_version)
              : '—';
            return (
              <tr
                key={a.id}
                onClick={() => onRowClick(a)}
                className="hover:bg-surface-700/40 cursor-pointer transition-colors border-b border-surface-600/20 last:border-0"
              >
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(a.id)}
                    className="w-3.5 h-3.5 rounded border-surface-600 bg-surface-700 text-brand-500 focus:ring-brand-500/40"
                  />
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${sev.dot}`} />
                </td>
                <td className="px-3 py-2.5">
                  <PlatformBadge platform={a.platform} />
                </td>
                <td className="px-3 py-2.5 max-w-[240px]">
                  <span className="text-xs text-gray-200 truncate block">
                    {a.message || a.alert_message || a.alert_type || 'Alert'}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="text-xs font-mono text-gray-400">{versionDisplay}</span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="text-xs text-gray-200">{formatNumber(a.value)}</span>
                  <span className="text-[10px] text-gray-500 mx-1">/</span>
                  <span className="text-xs text-gray-400">{formatNumber(a.threshold)}</span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${imp.cls}`}>
                    {imp.label}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-[11px] text-gray-500 whitespace-nowrap">
                  {relativeTime(a.created_at || a.triggered_at)}
                </td>
                <td className="px-3 py-2.5">
                  {a.acknowledged ? (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface-600/60 text-gray-400">
                      Ack
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-brand-500/15 text-brand-400">
                      New
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DetailPanel({ alert, detail, detailLoading, onClose, onAcknowledge }) {
  const [activeTab, setActiveTab] = useState('Overview');

  if (!alert) return null;

  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const imp = impactLevel(alert.impact);

  const platformCfg = PLATFORM_CONFIG[alert.platform];
  const versionDisplay = alert.app_version
    ? (alert.version_code ? `${alert.app_version} (${alert.version_code})` : alert.app_version)
    : '—';

  const quickStats = [
    { label: 'Metric Value', value: formatNumber(alert.value) },
    { label: 'Impact Score', value: `${alert.impact}` },
    { label: 'Platform', value: platformCfg ? platformCfg.label : 'All / Unknown', badge: alert.platform },
    { label: 'Affected Users', value: detail?.affectedUsers != null ? formatNumber(detail.affectedUsers) : '—' },
    { label: 'Version', value: versionDisplay },
    { label: 'Alert Type', value: (alert.alert_type || '—').replace(/_/g, ' ') },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-30" onClick={onClose} />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-[480px] bg-surface-800 border-l border-surface-600 shadow-2xl z-40 flex flex-col overflow-hidden animate-slide-in">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-surface-600/50 shrink-0">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase ${sev.badge}`}>
            {alert.severity}
          </span>
          <span className="text-sm text-gray-300 truncate flex-1">
            {alert.alert_type || alert.metric_name || 'Alert Detail'}
          </span>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {detailLoading && (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2">
            {quickStats.map((s) => (
              <div key={s.label} className="bg-surface-700/40 rounded-lg border border-surface-600/50 p-3">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">{s.label}</div>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold text-gray-100">{s.value}</div>
                  {s.badge && <PlatformBadge platform={s.badge} />}
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-surface-600/50 pb-0">
            {DETAIL_TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab
                    ? 'border-brand-500 text-brand-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'Overview' && (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">Alert Message</div>
                <p className="text-sm text-gray-200 leading-relaxed">
                  {alert.message || alert.alert_message || 'No message available'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">Metric Value</div>
                  <div className="text-lg font-bold text-gray-100">{formatNumber(alert.value)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">Threshold</div>
                  <div className="text-lg font-bold text-gray-400">{formatNumber(alert.threshold)}</div>
                </div>
              </div>
              {detail?.deviationMultiplier != null && (
                <div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">Deviation</div>
                  <span className={`inline-block px-2.5 py-1 rounded-lg text-sm font-bold ${imp.cls}`}>
                    {detail.deviationMultiplier.toFixed(1)}x threshold
                  </span>
                </div>
              )}
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">Triggered</div>
                <span className="text-xs text-gray-400">
                  {formatDateTime(alert.created_at || alert.triggered_at)}
                </span>
              </div>
            </div>
          )}

          {activeTab === 'Recommendations' && (
            <div className="space-y-3">
              {detail?.recommendations && detail.recommendations.length > 0 ? (
                detail.recommendations.map((rec, i) => (
                  <div
                    key={i}
                    className="bg-surface-700/40 rounded-lg border border-surface-600/50 p-3 space-y-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        rec.priority === 'high' ? 'bg-red-500/15 text-red-400' :
                        rec.priority === 'medium' ? 'bg-amber-500/15 text-amber-400' :
                        'bg-emerald-500/15 text-emerald-400'
                      }`}>
                        {rec.priority || 'medium'}
                      </span>
                      <span className="text-xs font-semibold text-gray-200">{rec.title || `Recommendation ${i + 1}`}</span>
                    </div>
                    <p className="text-[11px] text-gray-400 leading-relaxed">
                      {rec.description || rec.text || rec}
                    </p>
                    {rec.rootCause && (
                      <p className="text-[11px] text-gray-500 italic">Root cause: {rec.rootCause}</p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 py-4 text-center">No recommendations available</p>
              )}
            </div>
          )}

          {activeTab === 'Related' && (
            <div className="space-y-2">
              {detail?.relatedAlerts && detail.relatedAlerts.length > 0 ? (
                detail.relatedAlerts.map((ra, i) => {
                  const rSev = SEVERITY_CONFIG[ra.severity] || SEVERITY_CONFIG.info;
                  return (
                    <div
                      key={ra.id || i}
                      className="flex items-center gap-3 bg-surface-700/40 rounded-lg border border-surface-600/50 p-3"
                    >
                      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${rSev.dot}`} />
                      <span className="text-xs text-gray-200 truncate flex-1">
                        {ra.message || ra.alert_message || ra.alert_type || 'Related alert'}
                      </span>
                      <span className="text-[10px] text-gray-500 whitespace-nowrap">
                        {relativeTime(ra.created_at || ra.triggered_at)}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500 py-4 text-center">No related alerts found</p>
              )}
            </div>
          )}
        </div>

        {/* Footer: Acknowledge */}
        {!alert.acknowledged && (
          <div className="px-5 py-4 border-t border-surface-600/50 shrink-0">
            <button
              onClick={() => onAcknowledge(alert.id)}
              className="w-full py-2.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold transition-colors"
            >
              Acknowledge Alert
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function UnifiedAlertListPage() {
  // Filters
  const [severity, setSeverity] = useState('');
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  // Platform breakdown from API
  const [platformBreakdown, setPlatformBreakdown] = useState([]);

  // Data
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Detail panel
  const [activeAlert, setActiveAlert] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Debounced search
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  // ── Fetch alerts ─────────────────────────────────────────────────────────

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { days, limit: 200 };
      if (severity) params.severity = severity;
      if (showAcknowledged) params.acknowledged = true;
      if (debouncedSearch) params.search = debouncedSearch;
      if (platform) params.platform = platform;

      const { data } = await alertApi.getList(params);
      const list = Array.isArray(data) ? data : data?.alerts || [];
      setAlerts(list.map(enrichAlert));
      if (data?.platformBreakdown) {
        setPlatformBreakdown(data.platformBreakdown);
      }
    } catch (err) {
      console.error('Failed to load alerts', err);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [days, severity, showAcknowledged, debouncedSearch, platform]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // ── Filtered + sorted ────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const severityOrder = { critical: 0, warning: 1, info: 2 };
    return [...alerts].sort(
      (a, b) =>
        (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3) ||
        new Date(b.created_at || b.triggered_at || 0) - new Date(a.created_at || a.triggered_at || 0)
    );
  }, [alerts]);

  // ── Summary counts ───────────────────────────────────────────────────────

  const summary = useMemo(() => {
    const counts = { critical: 0, warning: 0, info: 0 };
    const topMsg = { critical: '', warning: '', info: '' };
    // Platform counts per severity: { critical: { mobile: 3, atv: 1, ... }, ... }
    const platCounts = { critical: {}, warning: {}, info: {} };
    for (const a of filtered) {
      const sev = a.severity || 'info';
      if (counts[sev] !== undefined) {
        counts[sev]++;
        if (!topMsg[sev]) topMsg[sev] = a.message || a.alert_message || a.alert_type || '';
        if (a.platform) {
          platCounts[sev][a.platform] = (platCounts[sev][a.platform] || 0) + 1;
        }
      }
    }
    return { counts, topMsg, platCounts };
  }, [filtered]);

  // ── Selection handlers ───────────────────────────────────────────────────

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((a) => a.id))
    );
  }, [filtered]);

  // ── Acknowledge ──────────────────────────────────────────────────────────

  const acknowledgeOne = useCallback(async (id) => {
    try {
      await alertApi.acknowledge(id);
      setActiveAlert(null);
      setDetail(null);
      fetchAlerts();
    } catch (err) {
      console.error('Acknowledge failed', err);
    }
  }, [fetchAlerts]);

  const bulkAcknowledge = useCallback(async () => {
    try {
      await alertApi.bulkAcknowledge([...selected]);
      setSelected(new Set());
      fetchAlerts();
    } catch (err) {
      console.error('Bulk acknowledge failed', err);
    }
  }, [selected, fetchAlerts]);

  // ── Detail panel ─────────────────────────────────────────────────────────

  const openDetail = useCallback(async (alert) => {
    setActiveAlert(alert);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await alertApi.getDetail(alert.id);
      setDetail(data);
    } catch (err) {
      console.error('Failed to load alert detail', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setActiveAlert(null);
    setDetail(null);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading && alerts.length === 0) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && alerts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 2l10 18H2L12 2z" />
        </svg>
        <p className="text-sm text-gray-400">{error}</p>
        <button
          onClick={fetchAlerts}
          className="px-4 py-1.5 rounded-lg bg-brand-500/20 text-brand-400 text-xs font-medium hover:bg-brand-500/30 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-500/20 rounded-md">
          <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0a3 3 0 11-6 0m6 0H9" />
          </svg>
        </div>
        <div>
          <h1 className="text-sm font-semibold text-gray-200">Alerts</h1>
          <p className="text-[11px] text-gray-500">
            {filtered.length} alert{filtered.length !== 1 ? 's' : ''} in the last {days} days
          </p>
        </div>
        {loading && (
          <div className="ml-2 w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {/* 1. Priority Summary Bar — with platform breakdown */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard severity="critical" count={summary.counts.critical} topMessage={summary.topMsg.critical} platformCounts={summary.platCounts.critical} />
        <SummaryCard severity="warning" count={summary.counts.warning} topMessage={summary.topMsg.warning} platformCounts={summary.platCounts.warning} />
        <SummaryCard severity="info" count={summary.counts.info} topMessage={summary.topMsg.info} platformCounts={summary.platCounts.info} />
      </div>

      {/* 2. Filter Bar */}
      <FilterBar
        severity={severity}
        setSeverity={setSeverity}
        days={days}
        setDays={setDays}
        search={search}
        setSearch={setSearch}
        platform={platform}
        setPlatform={setPlatform}
        showAcknowledged={showAcknowledged}
        setShowAcknowledged={setShowAcknowledged}
        selectedCount={selected.size}
        onBulkAcknowledge={bulkAcknowledge}
      />

      {/* 3. Alert Table */}
      <AlertTable
        alerts={filtered}
        selected={selected}
        onToggleSelect={toggleSelect}
        onToggleAll={toggleAll}
        onRowClick={openDetail}
      />

      {/* 4. Detail Panel */}
      {activeAlert && (
        <DetailPanel
          alert={activeAlert}
          detail={detail}
          detailLoading={detailLoading}
          onClose={closeDetail}
          onAcknowledge={acknowledgeOne}
        />
      )}
    </div>
  );
}
